import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';

const CHECK_OPTIONS = [
  { value: 'erfolgreich', label: 'Erfolgreich' },
  { value: 'eingeschraenkt', label: 'Eingeschränkt' },
  { value: 'fehlgeschlagen', label: 'Fehlgeschlagen' },
];
const OVERALL_STATUS_OPTIONS = [
  { value: 'erfolgreich', label: 'Erfolgreich' },
  { value: 'eingeschraenkt', label: 'Erfolgreich mit Einschränkung' },
  { value: 'fehlgeschlagen', label: 'Fehlgeschlagen' },
];
const RANSOMWARE_OPTIONS = [
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'nicht_aktiv', label: 'Nicht aktiv' },
  { value: 'nicht_geprueft', label: 'Nicht geprüft' },
];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
    {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
  </select>
);

const Input = ({ value, onChange, type = 'text', placeholder }) => (
  <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
);

export default function DRReportForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    customer_id: '',
    device_ninja_id: '',
    device_name: '',
    device_label: '',
    device_type: '',
    os_name: '',
    test_date: new Date().toISOString().split('T')[0],
    test_time: '08:00',
    test_duration: '',
    test_status: 'erfolgreich',
    backup_date: '',
    backup_software: '',
    ransomware_protection: 'nicht_geprueft',
    testmethod: 'Virtuelle Wiederherstellung',
    check_system_start: 'erfolgreich',
    check_login: 'erfolgreich',
    check_file_integrity: 'erfolgreich',
    check_apps_running: 'erfolgreich',
    notes: ''
  });

  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data));
    if (isEdit) {
      api.get(`/dr-reports/${id}`).then(r => {
        const d = r.data;
        setForm({
          customer_id: d.customer_id,
          device_ninja_id: d.device_ninja_id || '',
          device_name: d.device_name,
          device_label: d.device_label || '',
          device_type: d.device_type || '',
          os_name: d.os_name || '',
          test_date: d.test_date ? d.test_date.split('T')[0] : '',
          test_time: d.test_time || '',
          test_duration: d.test_duration || '',
          test_status: d.test_status,
          backup_date: d.backup_date ? d.backup_date.split('T')[0] : '',
          backup_software: d.backup_software || '',
          ransomware_protection: d.ransomware_protection || 'nicht_geprueft',
          testmethod: d.testmethod || 'Virtuelle Wiederherstellung',
          check_system_start: d.check_system_start,
          check_login: d.check_login,
          check_file_integrity: d.check_file_integrity,
          check_apps_running: d.check_apps_running,
          notes: d.notes || ''
        });
        if (d.customer_id) loadDevices(d.customer_id);
      });
    }
  }, [id]);

  const loadDevices = (customerId) => {
    api.get(`/devices?customer_id=${customerId}`).then(r => setDevices(r.data || [])).catch(() => setDevices([]));
  };

  const handleCustomer = (val) => {
    setForm(f => ({ ...f, customer_id: val, device_ninja_id: '', device_name: '', device_label: '', device_type: '', os_name: '' }));
    setDevices([]);
    if (val) loadDevices(val);
  };

  const handleDevice = (ninjaId) => {
    const dev = devices.find(d => String(d.id) === String(ninjaId));
    if (dev) {
      setForm(f => ({
        ...f,
        device_ninja_id: ninjaId,
        device_name: dev.system_name || dev.dns_name || '',
        device_label: dev.display_name || '',
        device_type: dev.node_class || '',
        os_name: ''
      }));
    } else {
      setForm(f => ({ ...f, device_ninja_id: ninjaId }));
    }
  };

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.customer_id || !form.device_name || !form.test_date || !form.backup_date || !form.test_duration) {
      setError('Bitte alle Pflichtfelder ausfüllen.'); return;
    }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await api.put(`/dr-reports/${id}`, form);
      } else {
        await api.post('/dr-reports', form);
      }
      navigate('/dr-reports');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dr-reports')} className="text-gray-400 hover:text-gray-600 text-sm">← Zurück</button>
        <h1 className="text-xl font-semibold text-gray-900">{isEdit ? 'DR-Bericht bearbeiten' : 'Neuer DR-Bericht'}</h1>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-6">

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-4 pb-2 border-b border-gray-100">Kundendaten & Gerät</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Kunde *">
              <Select value={form.customer_id} onChange={handleCustomer}
                options={[{ value: '', label: '— Kunde wählen —' }, ...customers.map(c => ({ value: c.id, label: c.name }))]} />
            </Field>
            <Field label="Gerät (NinjaOne)">
              <Select value={form.device_ninja_id} onChange={handleDevice}
                options={[{ value: '', label: devices.length ? '— Gerät wählen —' : '— Erst Kunde wählen —' }, ...devices.map(d => ({ value: d.id, label: d.system_name || d.display_name || d.dns_name || String(d.id) }))]} />
            </Field>
            <Field label="Gerätename *">
              <Input value={form.device_name} onChange={set('device_name')} placeholder="z.B. DESKTOP-JGB9II7" />
            </Field>
            <Field label="Bezeichnung">
              <Input value={form.device_label} onChange={set('device_label')} placeholder="z.B. Maschinenserver" />
            </Field>
            <Field label="Gerätetyp">
              <Input value={form.device_type} onChange={set('device_type')} placeholder="z.B. Windows Desktop" />
            </Field>
            <Field label="Betriebssystem">
              <Input value={form.os_name} onChange={set('os_name')} placeholder="z.B. Windows 11 Pro" />
            </Field>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-4 pb-2 border-b border-gray-100">Übungsdetails</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Datum der Übung *">
              <Input type="date" value={form.test_date} onChange={set('test_date')} />
            </Field>
            <Field label="Uhrzeit">
              <Input type="time" value={form.test_time} onChange={set('test_time')} />
            </Field>
            <Field label="Dauer (z.B. 10:22) *">
              <Input value={form.test_duration} onChange={set('test_duration')} placeholder="HH:MM" />
            </Field>
            <Field label="Backup vom *">
              <Input type="date" value={form.backup_date} onChange={set('backup_date')} />
            </Field>
            <Field label="Sicherungssoftware">
              <Input value={form.backup_software} onChange={set('backup_software')} placeholder="z.B. Veeam, Acronis, Wasabi" />
            </Field>
            <Field label="Ransomware-Schutz *">
              <Select value={form.ransomware_protection} onChange={set('ransomware_protection')} options={RANSOMWARE_OPTIONS} />
            </Field>
            <Field label="Testmethode">
              <Input value={form.testmethod} onChange={set('testmethod')} />
            </Field>
            <Field label="Gesamtergebnis">
              <Select value={form.test_status} onChange={set('test_status')} options={OVERALL_STATUS_OPTIONS} />
            </Field>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-4 pb-2 border-b border-gray-100">Testergebnisse</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Systemstart">
              <Select value={form.check_system_start} onChange={set('check_system_start')} options={CHECK_OPTIONS} />
            </Field>
            <Field label="Anmeldung">
              <Select value={form.check_login} onChange={set('check_login')} options={CHECK_OPTIONS} />
            </Field>
            <Field label="Dateiintegrität">
              <Select value={form.check_file_integrity} onChange={set('check_file_integrity')} options={CHECK_OPTIONS} />
            </Field>
            <Field label="Anwendungen lauffähig">
              <Select value={form.check_apps_running} onChange={set('check_apps_running')} options={CHECK_OPTIONS} />
            </Field>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-4 pb-2 border-b border-gray-100">Hinweise</h2>
          <textarea value={form.notes} onChange={e => set('notes')(e.target.value)}
            placeholder="Optionale Hinweise, z.B. BitLocker nicht aktiv..."
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button onClick={handleSubmit} disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
            {saving ? 'Speichert...' : isEdit ? 'Speichern' : 'Bericht erstellen'}
          </button>
          <button onClick={() => navigate('/dr-reports')}
            className="border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg transition-colors">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
